'use strict';

import BamTeamData from 'bam-team-data';
import LegacyData from './LegacyData';
const cloneDeep = BamTeamData.getHelpers().cloneDeep;

const CLIENT_ARR_DOT_NOTATION = 'team_info'; // teams arr found at window.team_info
const SERVER_ARR_DOT_NOTATION = 'res.locals.globalData.teamInfo.teams'; // teams arr found at req.res.locals.globalData.teamInfo.teams

/**
 *
 * @extends BamTeamData
 */
class MlbTeamData extends BamTeamData {

    /**
     * This class can be instantiated with a null or undefined windowOrReq, in which case the constructor assumes
     * that we are in a browser and our windowOrReq is the browser window object.  Thus, the following calls to this
     * constructor all result in the browser window object (not the controller req object) being used to initialize:
     *
     * - new MlbTeamData();
     * - new MlbTeamData(null, anArrayOfInactiveTeams);
     * - new MlbTeamData(window);
     *
     * @param windowOrReq A `window` or a BANE-controller `req`; optional (see above)
     * @param inactiveTeamsArr An array of inactive teams, if any, in Statsapi format
     * @param options Options to be passed through to the MlbTeamData constructor (this class's super()).
     */
    constructor(windowOrReq, inactiveTeamsArr) {
        const optionOverrides = {
            // Override default slug-deriving behavior because (as yet) statsapi doesn't return a franchise for
            // MLB teams:
            addSlugToActiveTeamsFn: function (team) {
                team.slug = team.teamName.toLowerCase().trim().replace(/[^a-z]/g, '');
            },
            // We're using NHL data with MLB code; causes issue because the Vegas team is inactive, as they've never
            // played a game, so, for now:
            inactivesAllowedInActiveArr: true,
            removeInactivesFromActiveArr: true
        };
        let ctx = windowOrReq;
        if (!ctx) {
            if (!(!window)) {
                ctx = window;
            } else {
                throw new Error('windowOrReq was null or undefined, but no window object was found.');
            }
        }

        let activeTeamsArr = BamTeamData.getTeamArrayFromReqOrWindow(ctx, CLIENT_ARR_DOT_NOTATION,
            SERVER_ARR_DOT_NOTATION);

        LegacyData.init(activeTeamsArr);
        super(
            activeTeamsArr,
            inactiveTeamsArr,
            optionOverrides
        );
    }

    /**
     * Passed a team ID, returns its tricode (its c_id, or more explicitly, its statsapi `fileCode`).  Note that this
     * is _not_ its `abbreviation` - e.g., the value returned for the Angels is 'ana', not 'laa'.
     * @param teamId
     * @return {string} The team's tricode, or 'mlb' if 0 is passed, or null otherwise
     */
    getTeamTricodeFromId(teamId) {
        if (teamId === 0) {
            return 'mlb';
        }
        var team = this._teamIdsToTeams[teamId];
        return (team ? team.fileCode : null);
    }

    /**
     * Get a base production URL for the JSP ("wired") site, e.g. http://newyork.yankees.mlb.com or http://mlb.mlb.com
     * @param teamIdOrTricode Pass 0 or 'mlb' for the league site
     * @return {string} A JSP-site URL, never ending with a forward-slash, or null if unrecognized id/tricode
     */
    getBaseJspUrl(teamIdOrTricode) {
        return this._getBaseLegacyUrl(teamIdOrTricode, true);
    }

    /**
     * Get a base production URL for the V2 ("responsive") site, e.g. http://m.yankees.mlb.com or http://m.mlb.com
     * @param teamIdOrTricode Pass 0 or 'mlb' for the league site
     * @return {string} A V2-site URL, never ending with a forward-slash, or null if unrecognized id/tricode
     */
    getBaseV2Url(teamIdOrTricode) {
        return this._getBaseLegacyUrl(teamIdOrTricode, false);
    }

    /**
     * Called by getBaseJspUrl() and getBaseV2Url()
     * @param teamIdOrTricode 0 or 'mlb' means league site
     * @param jsp True for JSP site, false for V2 site
     * @return {string} The URL, or null if unrecognized
     * @private
     */
    _getBaseLegacyUrl(teamIdOrTricode, jsp) {
        let id = parseInt(teamIdOrTricode);
        let tricode;
        if (!isNaN(id)) {
            if (id === 0) {
                tricode = 'mlb';
            } else {
                let team = this.getTeam(id);
                if (!team) {
                    // we don't recognize the tricode or id; not the end of the world - we'll use the league URL
                    // instead:
                    tricode = 'mlb';
                } else {
                    tricode = team.fileCode;
                }
            }
        } else {
            tricode = teamIdOrTricode;
        }
        return (LegacyData._getBaseUrlFromTricode(tricode, jsp) || null);
    }


    /**
     * Passed a tricode, return a teamCode (e.g. nym - nyn)
     * @param tricode Technically, the team's statsapi fileCode (ana not LAA; la not LAD; etc.)
     * @return {string} The team's statsapi teamCode (e.g. nyn - yes nyn, not nym - for Mets), or null if unrecognized
     * @private
     */
    static getTeamCodeFromTricode(tricode) {
        return LegacyData._getCodeFromTricode(tricode, 'teamCode');
    }

    /**
     * passed a tricode, return an abbreviation (e.g. la - LAD)
     * @param tricode Technically, the team's statsapi fileCode (ana not LAA; la not LAD; etc.), or null if unrecognized
     * @return {string} The team's statsapi abbreviation (e.g. NYM for Mets)
     * @private
     */
    static getAbbreviationFromTricode(tricode) {
        return LegacyData._getCodeFromTricode(tricode, 'abbreviation');
    }

}

export default MlbTeamData;
